import { defaultValues, useExternalScheduleForm } from '@hooks/forms/useExternalScheduleForm';
import { useToast } from '@hooks/useToast';
import { useTranslation } from 'react-i18next';
import { ExternalScheduleDialog } from '..';
import { useEffect, useState } from 'react';
import { Tabs } from '@components/ui/tabs';
import { options } from './constants';
import { useGetJobConcreteDirect } from '@services/jobs/concreteDirects';
import { Loader } from '@components/loader';
import { useDropdownsContext } from '@contexts/dropdowns';
import { DefaultExternalScheduleDialogProps } from '../types';
import { ExternalScheduleOrderDialogOption } from './types';
import { ExternalScheduleDateInputs } from '../../inputs/date';
import { ExternalScheduleMaterialInputs } from '../../inputs/material';
import { ExternalScheduleConcreteDirectInputs } from '../../inputs/concreteDirect';
import { ExternalScheduleContactsInputs } from '../../inputs/contacts';

export default function ExternalScheduleOrderDialog({
  open,
  setOpen,
  name,
  taskId,
  schedule,
  completed,
  importantTasks,
  jobId,
  concrete,
  note,
  concreteDirectId,
  address,
  geolocalization,
  additionalInputs,
  extraComment,
}: DefaultExternalScheduleDialogProps) {
  const { concreteMix, additive, setParams } = useDropdownsContext();
  const { t } = useTranslation('app');
  const { toast } = useToast();
  const [option, setOption] = useState<ExternalScheduleOrderDialogOption>(
    concreteDirectId ? 'concreteDirectOrder' : 'manualConcreteOrder',
  );

  const { data, isLoading } = useGetJobConcreteDirect(open, jobId, concreteDirectId);

  const { form, onSubmit, locked } = useExternalScheduleForm({
    concreteDirect: option === 'concreteDirectOrder',
    jobId,
    taskId,
    scheduleId: schedule?.id,
    note,
    successHandler: () => {
      form.reset();
      setOpen(false);
      toast({
        title: t(`messages.success.concreteOrder.${schedule?.id ? 'reschedule' : 'schedule'}`),
      });
    },
  });

  const { reset, getValues, watch } = form;
  const siteId = watch('externalSiteId');

  useEffect(() => {
    setParams({ products: { externalSiteId: siteId } });
  }, [setParams, siteId]);

  useEffect(() => {
    if (!open && !concreteDirectId) {
      reset(defaultValues);
      return;
    }
    if (!data?.data) return;
    const {
      externalSiteId,
      itemId,
      quantity,
      isExactQuantity,
      slump,
      orderItemAdditions: { itemId: additiveId, quantity: additiveQuantity },
      pourMethod,
      pourElement,
      spacingInMinutes,
      qtyPerHour,
      rateType,
      additionalInformation,
    } = data.data;

    reset({
      ...getValues(),
      externalSiteId,
      id: [itemId],
      quantity,
      isExactQuantity,
      slump,
      additive: {
        id: [additiveId],
        quantity: additiveQuantity,
      },
      pourMethod,
      pourElement,
      quantityRate: spacingInMinutes || qtyPerHour || '',
      rateType,
      additionalInformation,
    });
  }, [open, getValues, concreteDirectId, reset, data]);

  return (
    <ExternalScheduleDialog
      type={option}
      open={open}
      setOpen={setOpen}
      completed={completed}
      form={form}
      onSubmit={onSubmit}
      locked={locked}
      jobId={jobId}
      name={name}
      taskId={taskId}
      schedule={schedule}
      note={note}
      extraComment={extraComment}
      concreteDirectId={concreteDirectId}
      importantTasks={importantTasks}>
      <Tabs
        value={option}
        setValue={setOption}
        disabled={Boolean(schedule?.id)}
        options={options}
      />
      {option === 'manualConcreteOrder' && (
        <>
          <ExternalScheduleDateInputs form={form} disabled={completed} />
          {additionalInputs && <ExternalScheduleContactsInputs form={form} jobId={jobId} />}
          <ExternalScheduleMaterialInputs material={concrete} />
        </>
      )}
      {option === 'concreteDirectOrder' &&
        (concreteDirectId && (isLoading || concreteMix.disabled || additive.disabled) ? (
          <div className="relative h-8">
            <Loader local />
          </div>
        ) : (
          <ExternalScheduleConcreteDirectInputs
            form={form}
            disabled={completed || Boolean(concreteDirectId)}
            address={address}
            geolocalization={geolocalization}>
            <ExternalScheduleDateInputs
              form={form}
              disabled={completed || Boolean(concreteDirectId)}
            />
            <ExternalScheduleMaterialInputs material={concrete} />
          </ExternalScheduleConcreteDirectInputs>
        ))}
    </ExternalScheduleDialog>
  );
}
